import { User } from 'types';
import { isValid, differenceInDays } from 'date-fns';

export namespace Payment {
  const SERIAlIZED_CARD_KEY = 'fp_tmp_card';
  export interface BillingAddress {
    address: string;
    city: string;
    country: string; // code
    postCode: string;
  }

  export interface CardToOrder {
    cardId: string;
    cvc: string;
  }
  export interface CardResponse {
    status: string;
    card_id: string;
    validate_id: string;
  }

  export interface CardCredentials {
    cvv: string;
    month: string;
    year: string;
    number: number;
    card_holder?: string;
    zip?: string;
  }

  export interface ValidationResponse {
    status: string;
    result: ValidationModel;
  }

  export interface ValidationModel {
    card_id: string;
    validate_id: string;
  }

  export interface CheckSumRequest {
    sum: number;
    validation_id: string;
  }

  export interface CheckSumResponse {
    status: string;
  }

  export interface Fields {
    status: string;
    card_type: string;
    fields: FieldsDetails;
  }

  export interface FieldsDetails {
    postal_code: Field;
    address: Field;
    cvv: Field;
    card_holder: Field;
  }

  // export interface BraintreeModel {
  //   creditCard: {
  //     number: string;
  //     cvv: string;
  //     expirationDate: string;
  //     cardholderName?: string;
  //     billingAddress: {
  //       postalCode?: string;
  //       streetAddress?: string;
  //     }
  //   };
  //   options: {
  //     validate: boolean;
  //   };
  // }

  export interface Nonce {
    creditCards: CreditCards[];
  }

  export interface ThreeDSResponse {
    nonce: string;
  }

  export interface CreditCards {
    consumed: boolean;
    nonce: string;
  }

  export interface Field {
    name: string;
    type: string;
    layout?: string;
  }

  export class Card {
    number: FieldModel = new FieldModel();
    expiration: Expiration = new Expiration();
    cardholder: FieldModel = new FieldModel();
    isSubmitted: boolean = false;

    constructor(card?: Card) {
      if (card) {
        this.number = new FieldModel(card.number.value);
        this.expiration = new Expiration(
          card.expiration.mm.value,
          card.expiration.yy.value,
        );
        this.cardholder = new FieldModel(card.cardholder.value);
      }
    }

    isNumberValid(): boolean {
      if (!this.number.value) return false;
      if (this.number.value.length < 15) return false;
      if (!Card.checkLuhn(this.number.value)) return false;
      return true;
    }

    isCardholderValid(): boolean {
      if (!this.cardholder.value) return false;
      if (this.cardholder.value.length < 4) return false;
      return true;
    }

    static checkLuhn(number: string): boolean {
      const cardNumber = number.split(' ').join('');

      let sum = 0;
      let alternate = false;

      for (let i = cardNumber.length - 1; i >= 0; i--) {
        let n = parseInt(cardNumber.substring(i, i + 1));
        if (alternate) {
          n *= 2;
          if (n > 9) {
            n = (n % 10) + 1;
          }
        }
        sum += n;
        alternate = !alternate;
      }
      return sum % 10 === 0;
    }

    isValid(): boolean {
      return (
        this.isNumberValid() &&
        this.expiration.isExpirationValid() &&
        this.isCardholderValid()
      );
    }

    isInputValue(): boolean {
      if (
        !this.number.value ||
        !this.expiration.mm.value ||
        !this.expiration.yy.value ||
        !this.cardholder.value
      ) {
        return false;
      }

      return true;
    }

    cardMapper(nonce?: string): Object {
      const body = {
        card: {
          number: this.number.value,
          month: this.expiration.mm.value,
          year: `20${this.expiration.yy.value}`,
          card_holder: this.cardholder.value,
        },
        // nonce,
        // env: 'prod',
      };
      return body;
    }

    cardImage(): string {
      const cardNumber = this.number.value;
      if (cardNumber.startsWith('34') || cardNumber.startsWith('37')) {
        return 'am-ex';
      } else if (cardNumber.startsWith('4')) {
        return 'visa';
      } else if (cardNumber.startsWith('5')) {
        return 'master-card';
      } else {
        return 'credit-card';
      }
    }

    cardType(): string {
      const cardNumber = this.number.value;
      const binPrefix = parseInt(cardNumber.substring(0, 6));
      if (cardNumber.startsWith('4')) {
        return 'Visa';
      } else if (
        cardNumber.startsWith('51') ||
        cardNumber.startsWith('52') ||
        cardNumber.startsWith('53') ||
        cardNumber.startsWith('54') ||
        cardNumber.startsWith('55') ||
        (binPrefix >= 222100 && binPrefix <= 272099)
      ) {
        return 'Mastercard';
      } else if (cardNumber.startsWith('34') || cardNumber.startsWith('37')) {
        return 'American Express';
      } else if (
        cardNumber.startsWith('60') ||
        cardNumber.startsWith('62') ||
        cardNumber.startsWith('64') ||
        cardNumber.startsWith('65')
      ) {
        return 'Discover';
      } else if (cardNumber.startsWith('35')) {
        return 'JCB';
      } else if (
        cardNumber.startsWith('50') ||
        cardNumber.startsWith('56') ||
        cardNumber.startsWith('57') ||
        cardNumber.startsWith('58') ||
        cardNumber.startsWith('67')
      ) {
        return 'Maestro';
      } else {
        return 'Credit Card';
      }
    }

    temporaryToUserCard(id: string): User.Card {
      return {
        card_id: id,
        card_mask: this.number.value,
        card_type: this.cardType(),
        exp: this.expiration.mm.value + '/' + this.expiration.yy.value,
        exp_month: this.expiration.mm.value,
        exp_year: this.expiration.yy.value,
        temporary: this,
      };
    }

    toRequestModel(cvv: string) {
      const newCardRequestModel = {
        number: this.number.value,
        month: this.expiration.mm.value,
        year: `20${this.expiration.yy.value}`,
        cvv: cvv,
      };
      if (this.cardholder.value) {
        newCardRequestModel['card_holder'] = this.cardholder.value;
      }
      return newCardRequestModel;
    }

    serilizeCard(userId: string): string {
      return JSON.stringify({
        userId: userId,
        number: this.number.value,
        mm: this.expiration.mm.value,
        yy: this.expiration.yy.value,
        cardholder: this.cardholder.value,
      });
    }

    saveCard(userId: string): void {
      const cards = Payment.Card.getSavedCards(userId);

      const index = cards.findIndex(
        card => card.number.value === this.number.value,
      );

      if (index !== -1) {
        cards[index] = this;
      } else {
        cards.push(this);
      }

      localStorage.setItem(
        SERIAlIZED_CARD_KEY,
        JSON.stringify(cards.map(card => card.serilizeCard(userId))),
      );
    }

    static getSavedCards(userId: string): Card[] {
      const cards = localStorage.getItem(SERIAlIZED_CARD_KEY);

      if (!cards) return [];

      const parsedCards = JSON.parse(cards).map(card => {
        return JSON.parse(card);
      });

      const filteredCards = parsedCards.filter(card => card.userId === userId);

      return filteredCards.map(card => {
        const newCard = new Card();
        newCard.number.value = card.number;
        newCard.expiration.mm.value = card.mm;
        newCard.expiration.yy.value = card.yy;
        newCard.cardholder.value = card.cardholder;
        return newCard;
      });
    }

    static getSavedCardsAsUserCards(userId: string): User.Card[] {
      const savedCards = Card.getSavedCards(userId);

      return savedCards.map(card => {
        const year = `20${card.expiration.yy.value}`;
        const id = `${card.number.value}${card.expiration.mm.value}${year}`;

        return {
          card_id: id,
          card_mask: card.number.value,
          card_type: card.cardType(),
          exp: card.expiration.mm.value + '/' + card.expiration.yy.value,
          exp_month: card.expiration.mm.value,
          exp_year: year,
          temporary: card,
        };
      });
    }
  }

  export class FieldModel {
    value: string = '';
    error: string = '';
    required: boolean = true;
    placeholder: string = '';
    layout: string = '';

    constructor(value?: string) {
      this.value = value ?? '';
    }
  }

  export class Expiration {
    mm: FieldModel = new FieldModel();
    yy: FieldModel = new FieldModel();

    constructor(mm?: string, yy?: string) {
      this.mm = new FieldModel(mm ?? '');
      this.yy = new FieldModel(yy ?? '');
    }

    isMMValid(): boolean {
      if (!this.mm.value) return false;
      if (this.mm.value.length !== 2) return false;
      return true;
    }

    isYYValid(): boolean {
      if (!this.yy.value) return false;
      if (this.mm.value.length !== 2) return false;
      return true;
    }

    isExpirationValid(): boolean {
      if (!this.isMMValid() || !this.isYYValid()) return false;
      let date = new Date();
      date.setDate(25);
      date.setFullYear(2000 + parseInt(this.yy.value));
      date.setMonth(parseInt(this.mm.value) - 1);
      if (!isValid(date)) return false;
      if (differenceInDays(date, new Date()) < 0) return false;
      return true;
    }
  }
}
